import { useEffect, useMemo, useState } from "react";
import Modal from "@/components/base/Modal";
import { Field, Input, Select } from "@/components/base/FormControls";
import { TERRITORY_LEVEL_LABELS, type TerritoryLevel } from "@/store/territory/types";
import { useTerritory } from "@/store/territory/TerritoryProvider";

export interface TerritoryFormNode {
  id: string;
  nom: string;
  /** Identifiant du parent (regionId / provinceId / communeId). Vide pour une région. */
  parentId: string;
}

interface TerritoryFormProps {
  level: TerritoryLevel;
  open: boolean;
  onClose: () => void;
  node?: TerritoryFormNode | null;
}

const PARENT_LABELS: Record<TerritoryLevel, string> = {
  region: "",
  province: "Région",
  commune: "Province",
  secteur: "Commune / Ville",
};

function parentLevelOf(level: TerritoryLevel): TerritoryLevel | null {
  if (level === "province") return "region";
  if (level === "commune") return "province";
  if (level === "secteur") return "commune";
  return null;
}

/**
 * Création / modification d'un niveau territorial.
 * Le parent est toujours sélectionné dans une liste cohérente : une province
 * appartient à une région, une commune à une province, un secteur à une commune.
 */
export default function TerritoryForm({ level, open, onClose, node }: TerritoryFormProps) {
  const territory = useTerritory();
  const [nom, setNom] = useState("");
  const [parentId, setParentId] = useState("");
  const [errors, setErrors] = useState<Record<string, string>>({});

  const parentLevel = parentLevelOf(level);

  useEffect(() => {
    if (!open) return;
    setNom(node?.nom ?? "");
    setParentId(node?.parentId ?? "");
    setErrors({});
  }, [open, node]);

  const parentOptions = useMemo(() => {
    if (parentLevel === "region") {
      return territory.regions.map((region) => ({ value: region.id, label: region.nom }));
    }
    if (parentLevel === "province") {
      return territory.provinces.map((province) => ({
        value: province.id,
        label: `${province.nom} · ${territory.regionName(province.regionId)}`,
      }));
    }
    if (parentLevel === "commune") {
      return territory.communes.map((commune) => ({
        value: commune.id,
        label: `${commune.nom} · ${territory.provinceName(commune.provinceId)}`,
      }));
    }
    return [];
  }, [parentLevel, territory]);

  const isEdition = Boolean(node);
  const title = `${isEdition ? "Modifier" : "Ajouter"} — ${TERRITORY_LEVEL_LABELS[level].toLowerCase()}`;

  const handleSubmit = () => {
    const nextErrors: Record<string, string> = {};
    if (!nom.trim()) nextErrors.nom = "Le nom est obligatoire.";
    if (parentLevel && !parentId) nextErrors.parentId = "Sélectionnez le niveau parent.";
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) return;

    const trimmed = nom.trim();
    if (level === "region") {
      if (isEdition) territory.updateRegion(node!.id, trimmed);
      else territory.addRegion(trimmed);
    } else if (level === "province") {
      if (isEdition) territory.updateProvince(node!.id, { nom: trimmed, regionId: parentId });
      else territory.addProvince(trimmed, parentId);
    } else if (level === "commune") {
      if (isEdition) territory.updateCommune(node!.id, { nom: trimmed, provinceId: parentId });
      else territory.addCommune(trimmed, parentId);
    } else {
      if (isEdition) territory.updateSecteur(node!.id, { nom: trimmed, communeId: parentId });
      else territory.addSecteur(trimmed, parentId);
    }
    onClose();
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      size="md"
      title={title}
      description="Le rattachement au niveau parent garantit une cascade territoriale cohérente."
      footer={
        <>
          <button
            type="button"
            onClick={onClose}
            className="inline-flex items-center justify-center whitespace-nowrap rounded-md border border-foreground-200 bg-background-50 px-5 py-2.5 text-sm font-label font-medium text-foreground-900 transition-colors hover:bg-background-100 cursor-pointer"
          >
            Annuler
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-label font-medium text-background-50 transition-colors hover:bg-primary-600 cursor-pointer"
          >
            <i className="ri-save-line text-base" aria-hidden="true" />
            {isEdition ? "Enregistrer" : "Ajouter"}
          </button>
        </>
      }
    >
      <div className="flex flex-col gap-3.5">
        {parentLevel && (
          <Field label={PARENT_LABELS[level]} htmlFor="territory-parent" required error={errors.parentId}>
            <Select
              id="territory-parent"
              value={parentId}
              onChange={(event) => setParentId(event.target.value)}
              options={parentOptions}
              placeholder={`Sélectionnez ${PARENT_LABELS[level].toLowerCase()}`}
              invalid={Boolean(errors.parentId)}
            />
          </Field>
        )}
        <Field label="Nom" htmlFor="territory-nom" required error={errors.nom}>
          <Input
            id="territory-nom"
            value={nom}
            onChange={(event) => setNom(event.target.value)}
            invalid={Boolean(errors.nom)}
            maxLength={80}
          />
        </Field>
      </div>
    </Modal>
  );
}